import type { BuyOutcome, Deals, DealsContext, LevelCatalog } from "@highmarch/app";
import type { GearItem, GemPackData, GrantedItems } from "@highmarch/domain";
import type { HubApi } from "../../features/registry";
import { uiUrl } from "../portraits";

/**
 * How the Deals screen reaches the game (SHOP-UI; the feature features/deals.ts sets it once at boot, like
 * economyLink.ts): the `Deals` use cases, the context paid offers are shown in, the store's localised prices and the
 * two purchase paths (a Deals offer, a gem pack). Null in tests and before boot: then there is no Deals entry at all.
 */

export type DealBuyOutcome =
  | { readonly status: "granted"; readonly granted: GrantedItems; readonly vipPoints: number; readonly bankedGear: readonly GearItem[] }
  | { readonly status: "pending" | "cancelled" | "duplicate" }
  | { readonly status: "refused" | "failed"; readonly reason: string };

export interface DealsLink {
  readonly deals: Deals;
  readonly catalog: LevelCatalog;
  now(): number;
  /** Paid offers allowed (a store is open: StoreKit, or the web's sandbox with ?debug/?sandboxstore) and the storefront. */
  ctx(): DealsContext;
  /** Which store sells: the web's test store gets its own confirm and label. */
  readonly storeKind: "sandbox" | "storekit2" | null;
  /** Product id → the store's own formatted price (StoreKit localises); empty until the store answers. */
  prices(): Promise<ReadonlyMap<string, string>>;
  /** Buys a Deals offer: prepare (the intent) → save → the store → grant once → save → finish. */
  buy(offerId: string): Promise<DealBuyOutcome>;
  /** The gem packs (shop.json), bought through PurchaseService. */
  gemPacks(): readonly GemPackData[];
  buyGems(packId: string): Promise<BuyOutcome>;
  /** Save and redraw the hub (its pills, the Deals badge). */
  changed(): void;
  /** The hub while it is up (null in a level): Deals opens only from the hub. */
  hub: HubApi | null;
}

let link: DealsLink | null = null;
export function setDealsLink(l: DealsLink | null): void { link = l; }
export function dealsLink(): DealsLink | null { return link; }

/**
 * A tab another feature adds at the end of the Deals strip, opening its own screen instead of a Deals page (the War
 * Campaign pass, MP-22: players browsing Deals find it). Its tap closes Deals and calls `open`.
 */
export interface DealsExtraTab {
  readonly id: string;
  readonly label: string;
  /** Painted icon path under art/interface ("icons/deals_pass.png"). */
  readonly icon: string;
  dot(): number;
  visible(): boolean;
  open(): void;
  /** Draws the tab as a Store page inside `host` (the Store keeps its header and tabs); without it the tap calls open. */
  mount?(host: HTMLElement): void;
}
const extraTabs: DealsExtraTab[] = [];
/** The strip key of an extra tab (never a Deals page id). */
const EXTRA_KEY = "x:";
export function addDealsTab(t: DealsExtraTab): void { if (!extraTabs.some((x) => x.id === t.id)) extraTabs.push(t); }
/** The visible extra tabs as the strip's icon tabs (ui/components.ts IconTab). */
export function extraTabViews(): { key: string; label: string; icon: string; dot: number; className: string; data: string }[] {
  return extraTabs.filter((t) => t.visible()).map((t) => ({ key: `${EXTRA_KEY}${t.id}`, label: t.label, icon: uiUrl(t.icon), dot: t.dot(), className: "dtab dxtab", data: `data-xtab="${t.id}"` }));
}
/** The visible extra tabs' red dots together (the hub's Store button adds them to its badge). */
export function extraTabsDot(): number { return extraTabs.filter((t) => t.visible()).reduce((n, t) => n + t.dot(), 0); }
/** The extra tab a strip key names, or null for a Deals page. */
export function extraTab(key: string): DealsExtraTab | null { return extraTabs.find((t) => `${EXTRA_KEY}${t.id}` === key && t.visible()) ?? null; }
